import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import purchaseDealSheetService, {
  PAYMENT_STATUS_LABELS,
  TRANSACTION_TYPE_LABELS,
} from '../../../../services/purchaseDealSheetService';
import PaymentFormModal from './PaymentFormModal';
import SupplierDealSheetUploadModal from './SupplierDealSheetUploadModal';
import {
  BUYER_SNAPSHOT_FIELDS,
  MODE_OF_DELIVERY_OPTIONS,
  PAYMENT_STATUS_COLORS,
  SELLER_SNAPSHOT_FIELDS,
  TRANSACTION_TYPE_OPTIONS,
} from './purchaseDealSheet.constants';
import {
  apiErrMsg,
  canDeleteDealSheet,
  canEditDealSheet,
  formatCurrency,
  formatDate,
  formatDateTime,
  getRemainingPayable,
  isFullyPaid,
  filenameFromUrl,
  toNumber,
} from './purchaseDealSheet.helpers';

const FULL_WIDTH = { width: '100%' };

function mapEditFormToFields(editForm) {
  if (!editForm) return {};
  const basic = editForm.basic_information || {};
  const financial = editForm.financial_details || {};
  const security = editForm.security_information || {};
  return {
    transaction_type: basic.transaction_type,
    transaction_date: basic.transaction_date ? dayjs(basic.transaction_date) : null,
    payment_date: basic.payment_date ? dayjs(basic.payment_date) : null,
    mode_of_delivery: basic.mode_of_delivery,
    quantity: financial.quantity,
    price_per_share: financial.price_per_share,
    stamp_duty_percent: financial.stamp_duty_percent,
    tds_percent: financial.tds_percent,
    security_name: security.security_name,
    isin: security.isin,
  };
}

function SectionHeading({ children, icon }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-gray-100 mb-4">
      {icon && <span className="text-gray-400 text-xs">{icon}</span>}
      <p className="m-0 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {children}
      </p>
    </div>
  );
}

function MetricCard({ label, value, valueClass = '' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex flex-col gap-0.5 min-w-0">
      <p className="text-[11px] text-gray-400 font-medium m-0 uppercase tracking-wide">{label}</p>
      <p className={`text-base font-bold text-gray-900 m-0 truncate ${valueClass}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <p className="text-[11px] text-gray-400 font-medium m-0 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-800 font-medium m-0 break-all">{value ?? '—'}</p>
    </div>
  );
}

function SnapshotGrid({ fields, data }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
      {fields.map(({ key, label }) => (
        <InfoRow key={key} label={label} value={data?.[key] ?? '—'} />
      ))}
    </div>
  );
}

function EditFormSection({ title, children }) {
  return (
    <div className="mb-5">
      <SectionHeading>{title}</SectionHeading>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-0">{children}</div>
    </div>
  );
}

export default function PurchaseDealSheetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_MANAGE);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dealSheet, setDealSheet] = useState(null);
  const [editForm] = Form.useForm();

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [supplierUploadOpen, setSupplierUploadOpen] = useState(false);

  const fullyPaid = isFullyPaid(dealSheet);
  const editable = canManage && canEditDealSheet(dealSheet);
  const remaining = getRemainingPayable(dealSheet);

  const loadDealSheet = useCallback(async () => {
    setLoading(true);
    try {
      const data = await purchaseDealSheetService.getDealSheetById(id);
      setDealSheet(data);
      editForm.setFieldsValue(mapEditFormToFields(data?.edit_form));
    } catch (e) {
      message.error(apiErrMsg(e));
      setDealSheet(null);
    } finally {
      setLoading(false);
    }
  }, [id, editForm]);

  useEffect(() => { loadDealSheet(); }, [loadDealSheet]);

  const handleDelete = async () => {
    try {
      await purchaseDealSheetService.deleteDealSheet(id);
      message.success('Purchase deal sheet deleted');
      navigate('/pre-ipo/purchase-deal-sheet');
    } catch (e) {
      message.error(apiErrMsg(e));
    }
  };

  const handleGenerateDealSheet = async () => {
    const isRegenerate = Boolean(dealSheet?.pdf_url);
    if (isRegenerate) {
      Modal.confirm({
        title: 'Regenerate deal sheet PDF?',
        content: 'This will overwrite the existing PDF with current deal sheet data.',
        okText: 'Regenerate',
        onOk: async () => {
          setGenerating(true);
          try {
            const result = await purchaseDealSheetService.generateDealSheetPdf(id, {
              force_regenerate: true,
            });
            message.success('Deal sheet PDF regenerated');
            if (result?.url) window.open(result.url, '_blank', 'noopener,noreferrer');
            await loadDealSheet();
          } catch (e) {
            message.error(apiErrMsg(e));
          } finally {
            setGenerating(false);
          }
        },
      });
      return;
    }

    setGenerating(true);
    try {
      const result = await purchaseDealSheetService.generateDealSheetPdf(id);
      message.success('Deal sheet PDF generated');
      if (result?.url) window.open(result.url, '_blank', 'noopener,noreferrer');
      await loadDealSheet();
    } catch (e) {
      message.error(apiErrMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const buildPatchPayload = (values) => {
    const payload = {};
    const assign = (key, val) => {
      if (val !== undefined && val !== null && val !== '') payload[key] = val;
    };
    assign('transaction_type', values.transaction_type);
    assign('mode_of_delivery', values.mode_of_delivery);
    if (values.transaction_date) assign('transaction_date', values.transaction_date.format('YYYY-MM-DD'));
    if (values.payment_date) assign('payment_date', values.payment_date.format('YYYY-MM-DD'));
    assign('quantity', values.quantity);
    assign('price_per_share', values.price_per_share);
    assign('stamp_duty_percent', values.stamp_duty_percent);
    assign('tds_percent', values.tds_percent);
    assign('security_name', values.security_name);
    assign('isin', values.isin);
    return payload;
  };

  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = buildPatchPayload(values);
      if (Object.keys(payload).length === 0) { message.info('No changes to save'); return; }
      setSaving(true);
      const updated = await purchaseDealSheetService.updateDealSheet(id, payload);
      setDealSheet(updated);
      editForm.setFieldsValue(mapEditFormToFields(updated?.edit_form));
      message.success('Deal sheet updated');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentSubmit = async (payload) => {
    setPaymentSubmitting(true);
    try {
      let updated;
      if (paymentTarget) {
        updated = await purchaseDealSheetService.updatePayment(id, paymentTarget.uuid ?? paymentTarget.id, payload);
      } else {
        updated = await purchaseDealSheetService.createPayment(id, payload);
      }
      setDealSheet(updated);
      editForm.setFieldsValue(mapEditFormToFields(updated?.edit_form));
      setPaymentModalOpen(false);
      setPaymentTarget(null);
      message.success(paymentTarget ? 'Payment updated' : 'Payment added');
    } catch (e) {
      message.error(apiErrMsg(e));
      throw e;
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    try {
      const updated = await purchaseDealSheetService.deletePayment(id, payment.uuid ?? payment.id);
      setDealSheet(updated);
      editForm.setFieldsValue(mapEditFormToFields(updated?.edit_form));
      message.success('Payment removed');
    } catch (e) {
      message.error(apiErrMsg(e));
    }
  };

  const handleSupplierUploadSuccess = (result) => {
    const nextUrl = result?.supplier_deal_sheet_url ?? result?.url;
    if (typeof nextUrl === 'string') {
      setDealSheet((prev) => (prev ? { ...prev, supplier_deal_sheet_url: nextUrl } : prev));
      return;
    }
    if (result && typeof result === 'object') {
      setDealSheet(result);
    }
  };

  const paymentColumns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      width: 120,
      render: (v) => formatDate(v),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v) => <span className="font-semibold">{formatCurrency(v)}</span>,
    },
    {
      title: 'UTR',
      dataIndex: 'utr_number',
      key: 'utr_number',
      width: 160,
      render: (v) => v ? <span className="font-mono text-xs">{v}</span> : <span className="text-gray-400">—</span>,
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (v) => v || <span className="text-gray-400">—</span>,
    },
    {
      title: 'Recorded By',
      dataIndex: 'recorded_by_name',
      key: 'recorded_by_name',
      width: 140,
      render: (v) => v || <span className="text-gray-400">—</span>,
    },
    ...(canManage && !fullyPaid ? [{
      title: '',
      key: 'actions',
      width: 120,
      align: 'right',
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => { setPaymentTarget(row); setPaymentModalOpen(true); }}
            className="text-gray-500 hover:text-primary-600"
          />
          <Popconfirm title="Remove this payment?" onConfirm={() => handleDeletePayment(row)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    }] : []),
  ], [canManage, fullyPaid, id]);

  // ─── Loading / Error states ────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardPageWrapper>
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
          <Spin size="large" />
          <p className="text-sm text-gray-400">Loading deal sheet…</p>
        </div>
      </DashboardPageWrapper>
    );
  }

  if (!dealSheet) {
    return (
      <DashboardPageWrapper>
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
          <ExclamationCircleOutlined className="text-4xl text-gray-300" />
          <p className="text-gray-500 m-0">Deal sheet not found.</p>
          <Button onClick={() => navigate('/pre-ipo/purchase-deal-sheet')}>Back to list</Button>
        </div>
      </DashboardPageWrapper>
    );
  }

  const statusColor = PAYMENT_STATUS_COLORS[dealSheet.payment_status];
  const statusLabel = PAYMENT_STATUS_LABELS[dealSheet.payment_status] || dealSheet.payment_status;
  const payments = Array.isArray(dealSheet.payments) ? dealSheet.payments : [];

  const sellerData = dealSheet.edit_form?.seller_information ?? {};
  const buyerData = dealSheet.edit_form?.buyer_information ?? {};
  const basicData = dealSheet.edit_form?.basic_information ?? {};
  const financialData = dealSheet.edit_form?.financial_details ?? {};
  const securityData = dealSheet.edit_form?.security_information ?? {};

  return (
    <DashboardPageWrapper>
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 pt-1 pb-8 flex flex-col gap-5">

        {/* ─── Page Header ─────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/pre-ipo/purchase-deal-sheet')}
              className="shrink-0 -ml-1 mt-0.5 px-1.5 text-gray-500 hover:text-gray-900"
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 m-0 leading-tight">
                  Deal Sheet{' '}
                  <span className="text-gray-400 font-mono text-sm">
                    #{String(dealSheet.id).slice(0, 8)}
                  </span>
                </h1>
                <Tag
                  color={statusColor}
                  className="m-0 text-xs font-semibold uppercase tracking-wide"
                >
                  {statusLabel}
                </Tag>
                {dealSheet.pdf_generated && (
                  <Tag icon={<FilePdfOutlined />} color="blue" className="m-0 text-xs">
                    PDF Generated
                  </Tag>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5 mb-0 truncate">
                {TRANSACTION_TYPE_LABELS[dealSheet.transaction_type] ?? dealSheet.transaction_type}
                {dealSheet.supplier_name ? ` · ${dealSheet.supplier_name}` : ''}
                {dealSheet.transaction_date ? ` · ${formatDate(dealSheet.transaction_date)}` : ''}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {dealSheet.pdf_url ? (
              <Button
                href={dealSheet.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                icon={<FilePdfOutlined />}
                className="border-gray-200 text-gray-700"
              >
                View PDF
              </Button>
            ) : null}
            {dealSheet.supplier_deal_sheet_url ? (
              <Button
                href={dealSheet.supplier_deal_sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                icon={<FileTextOutlined />}
                className="border-gray-200 text-gray-700"
              >
                View supplier sheet
              </Button>
            ) : null}
            {canManage && (
              <Button
                icon={<UploadOutlined />}
                onClick={() => setSupplierUploadOpen(true)}
                className="border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-600"
              >
                {dealSheet.supplier_deal_sheet_url ? 'Replace supplier sheet' : 'Upload supplier sheet'}
              </Button>
            )}
            {canManage && (
              <Button
                icon={<FilePdfOutlined />}
                loading={generating}
                onClick={handleGenerateDealSheet}
                className="border-gray-200 text-gray-700 hover:border-green-500 hover:text-green-600"
              >
                {dealSheet.pdf_url ? 'Regenerate PDF' : 'Generate Deal Sheet'}
              </Button>
            )}
            {canManage && canDeleteDealSheet(dealSheet) && (
              <Popconfirm
                title="Delete deal sheet?"
                description="This action cannot be undone."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button danger icon={<DeleteOutlined />} className="border-red-200">
                  Delete
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>

        {/* ─── Status banners ───────────────────────────────────────────────── */}
        {fullyPaid && (
          <div className="flex items-center gap-2.5 rounded-xl bg-green-50 border border-green-100 px-4 py-3 text-sm text-green-700">
            <CheckCircleOutlined className="text-green-500 text-base shrink-0" />
            <span>This deal sheet is <strong>fully paid</strong> and locked for editing.</span>
          </div>
        )}

        {/* ─── Key metrics strip ────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total Payable" value={formatCurrency(dealSheet.total_payable)} />
          <MetricCard label="Total Paid" value={formatCurrency(dealSheet.total_paid_amount)} valueClass="text-green-600" />
          <MetricCard
            label="Remaining"
            value={formatCurrency(remaining)}
            valueClass={remaining > 0 ? 'text-orange-600' : 'text-gray-400'}
          />
          <MetricCard label="Installments" value={String(toNumber(dealSheet.payments_count))} />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Supplier deal sheet
              </p>
              {dealSheet.supplier_deal_sheet_url ? (
                <p className="m-0 mt-1 text-sm font-medium text-gray-800 truncate">
                  {filenameFromUrl(dealSheet.supplier_deal_sheet_url)}
                </p>
              ) : (
                <p className="m-0 mt-1 text-sm text-gray-400">Not uploaded yet</p>
              )}
            </div>
            {dealSheet.supplier_deal_sheet_url ? (
              <a
                href={dealSheet.supplier_deal_sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 sm:mt-0"
              >
                <FileTextOutlined />
                View file
              </a>
            ) : null}
          </div>
        </div>

        {/* ─── Main content ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left — 2/3 wide */}
          <div className="lg:col-span-2 flex flex-col gap-5">

            {/* Overview card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeading>Overview</SectionHeading>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                <InfoRow label="Security" value={dealSheet.security_name} />
                <InfoRow label="ISIN" value={dealSheet.isin} />
                <InfoRow label="Supplier" value={dealSheet.supplier_name} />
                <InfoRow label="Quantity" value={dealSheet.quantity != null ? String(dealSheet.quantity) : '—'} />
                <InfoRow label="Price / Share" value={formatCurrency(dealSheet.price_per_share)} />
                <InfoRow label="Delivery" value={dealSheet.mode_of_delivery_label || dealSheet.mode_of_delivery} />
                <InfoRow label="Created" value={formatDateTime(dealSheet.created_at)} />
                <InfoRow label="Updated" value={formatDateTime(dealSheet.updated_at)} />
              </div>
            </div>

            {/* Payments card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="m-0 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  Payments
                </p>
                {canManage && !fullyPaid && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => { setPaymentTarget(null); setPaymentModalOpen(true); }}
                    disabled={remaining <= 0}
                  >
                    Add Payment
                  </Button>
                )}
              </div>
              <Table
                rowKey={(r) => r.uuid || r.id}
                columns={paymentColumns}
                dataSource={payments}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                className="[&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
                locale={{ emptyText: (
                  <div className="py-6 text-sm text-gray-400">No payments recorded yet</div>
                )}}
              />
            </div>

            {/* Buyer & Seller — always read-only */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeading>Buyer Information</SectionHeading>
              <SnapshotGrid fields={BUYER_SNAPSHOT_FIELDS} data={buyerData} />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeading>Seller (Supplier) Information</SectionHeading>
              <SnapshotGrid fields={SELLER_SNAPSHOT_FIELDS} data={sellerData} />
            </div>

            <div className="border-t border-gray-200" />

            {/* Financial & Basic — editable until fully paid */}
            {editable ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <SectionHeading>Edit Deal Sheet</SectionHeading>
                <Form
                  form={editForm}
                  layout="vertical"
                  requiredMark={false}
                  className="[&_.ant-form-item]:w-full [&_.ant-form-item-control-input-content]:w-full"
                >
                  <EditFormSection title="Financial Details">
                    <Form.Item name="quantity" label="Quantity">
                      <InputNumber style={FULL_WIDTH} min={0.0001} />
                    </Form.Item>
                    <Form.Item name="price_per_share" label="Price per Share">
                      <InputNumber style={FULL_WIDTH} min={0.0001} precision={2} />
                    </Form.Item>
                    <Form.Item name="stamp_duty_percent" label="Stamp Duty (%)">
                      <InputNumber style={FULL_WIDTH} min={0} />
                    </Form.Item>
                    <Form.Item name="tds_percent" label="TDS (%)">
                      <InputNumber style={FULL_WIDTH} min={0} />
                    </Form.Item>
                    <Form.Item name="security_name" label="Security Name">
                      <Input />
                    </Form.Item>
                    <Form.Item name="isin" label="ISIN">
                      <Input />
                    </Form.Item>
                  </EditFormSection>

                  <EditFormSection title="Basic Information">
                    <Form.Item name="transaction_type" label="Transaction Type">
                      <Select style={FULL_WIDTH} options={TRANSACTION_TYPE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="mode_of_delivery" label="Mode of Delivery">
                      <Select style={FULL_WIDTH} options={MODE_OF_DELIVERY_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="transaction_date" label="Transaction Date">
                      <DatePicker style={FULL_WIDTH} format="DD MMM YYYY" />
                    </Form.Item>
                    <Form.Item name="payment_date" label="Payment Date">
                      <DatePicker style={FULL_WIDTH} format="DD MMM YYYY" />
                    </Form.Item>
                  </EditFormSection>

                  <div className="flex justify-end border-t border-gray-100 pt-4 mt-2">
                    <Button type="primary" loading={saving} onClick={handleSaveEdit}>
                      Save Changes
                    </Button>
                  </div>
                </Form>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <SectionHeading>Financial Details</SectionHeading>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                    <InfoRow label="Quantity" value={financialData.quantity != null ? String(financialData.quantity) : dealSheet.quantity != null ? String(dealSheet.quantity) : '—'} />
                    <InfoRow label="Price per Share" value={formatCurrency(financialData.price_per_share ?? dealSheet.price_per_share)} />
                    <InfoRow label="Stamp Duty (%)" value={financialData.stamp_duty_percent != null ? String(financialData.stamp_duty_percent) : '—'} />
                    <InfoRow label="TDS (%)" value={financialData.tds_percent != null ? String(financialData.tds_percent) : '—'} />
                    <InfoRow label="Security Name" value={securityData.security_name ?? dealSheet.security_name} />
                    <InfoRow label="ISIN" value={securityData.isin ?? dealSheet.isin} />
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <SectionHeading>Basic Information</SectionHeading>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                    <InfoRow label="Transaction Type" value={TRANSACTION_TYPE_LABELS[basicData.transaction_type ?? dealSheet.transaction_type] ?? basicData.transaction_type ?? dealSheet.transaction_type} />
                    <InfoRow label="Mode of Delivery" value={dealSheet.mode_of_delivery_label || basicData.mode_of_delivery || dealSheet.mode_of_delivery} />
                    <InfoRow label="Transaction Date" value={formatDate(basicData.transaction_date ?? dealSheet.transaction_date)} />
                    <InfoRow label="Payment Date" value={formatDate(basicData.payment_date ?? dealSheet.payment_date)} />
                    <InfoRow label="PDF Generated" value={basicData.pdf_generated ?? dealSheet.pdf_generated ? 'Yes' : 'No'} />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right sidebar — sticky */}
          <div className="lg:col-span-1 flex flex-col gap-5 lg:sticky lg:top-4 lg:self-start">

            {/* Financial breakdown */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeading>Financial Summary</SectionHeading>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Consideration', value: dealSheet.consideration ?? dealSheet.total_consideration },
                  { label: 'Stamp Duty', value: dealSheet.stamp_duty_amount },
                  { label: 'TDS', value: dealSheet.tds_amount },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="text-gray-800">{formatCurrency(value)}</span>
                  </div>
                ))}
                <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700">Total Payable</span>
                  <span className="text-base font-bold text-gray-900">{formatCurrency(dealSheet.total_payable)}</span>
                </div>
              </div>
            </div>

            {/* Payment progress */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeading>Payment Progress</SectionHeading>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Total Paid</span>
                  <span className="text-green-600 font-semibold">{formatCurrency(dealSheet.total_paid_amount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Remaining</span>
                  <span className={`font-semibold ${remaining > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                    {formatCurrency(remaining)}
                  </span>
                </div>
                {/* Progress bar */}
                {toNumber(dealSheet.total_payable) > 0 && (
                  <div className="mt-1">
                    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-green-500 transition-all"
                        style={{
                          width: `${Math.min(100, (toNumber(dealSheet.total_paid_amount) / toNumber(dealSheet.total_payable)) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-[11px] text-gray-400 text-right mt-1 m-0">
                      {Math.round((toNumber(dealSheet.total_paid_amount) / toNumber(dealSheet.total_payable)) * 100)}% paid
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm border-t border-gray-100 pt-3">
                  <span className="text-gray-500">Installments</span>
                  <span className="font-semibold">{toNumber(dealSheet.payments_count)}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <PaymentFormModal
        open={paymentModalOpen}
        dealSheet={dealSheet}
        payment={paymentTarget}
        loading={paymentSubmitting}
        onCancel={() => { setPaymentModalOpen(false); setPaymentTarget(null); }}
        onSubmit={handlePaymentSubmit}
      />

      <SupplierDealSheetUploadModal
        open={supplierUploadOpen}
        onClose={() => setSupplierUploadOpen(false)}
        dealSheetId={id}
        supplierDealSheetUrl={dealSheet?.supplier_deal_sheet_url}
        onSuccess={handleSupplierUploadSuccess}
      />
    </DashboardPageWrapper>
  );
}
